import { useEffect } from 'react';
import { loadOverlays, reportOverlayFailure, useOverlays } from '../../components/DeferredOverlay';
import { isEditableTarget, opensSearch } from './hotkey';
import { setSearchOpen, useSearchOpen } from './open';

/**
 * Universal search — Ctrl+K.
 *
 * ── WHAT IT IS FOR ────────────────────────────────────────────────────────────────────────────
 *
 * A property manager on the phone has a NUMBER in front of them. It is on a bond lodgement form,
 * in an owner's email, or being read out by a tenant: TE000003, BL000112, the Tenancy Services
 * reference on a bond. Before this box, reaching that record meant knowing which of eleven list
 * screens issues that prefix and then searching within it — so the number, which is the most
 * precise thing anybody has, was the slowest way in. Now it is the fastest: one indexed lookup,
 * and the record opens.
 *
 * Everything else is the same box doing the ordinary thing — an address, a surname, an invoice
 * number, a filename — across every record the agency has, ranked by `rankHits` in core.
 *
 * ── THIS FILE IS THE SHELL. THE BOX ITSELF IS `SearchPanel.tsx`, AND LAZY ─────────────────────
 *
 * The split is a bundle decision (DESIGN_SYSTEM.md §8). This component is mounted by the
 * application shell, so it is on every page, so everything it imports statically lands in the
 * chunk every session downloads — and the eager budget has been breached since M3, which the §8
 * log is explicit nobody should treat as headroom. Measured three ways on the same machine and the
 * same build, app chunk gzip: the panel imported statically **+2.73 KB** eager; behind this
 * `lazy()` **+1.23 KB**; and **+0.88 KB** once `@promanage/core/search` came off the core barrel
 * too, which is where the remaining 0.35 KB was hiding. The panel lands as its own **2.85 KB**
 * chunk. What is still eager is this file, the key listener, the store and the two triggers — and
 * every one of those has to exist before anybody presses anything.
 *
 * None of it is paid for in latency, because of the prefetch below.
 */

export function UniversalSearch() {
  const open = useSearchOpen();
  const overlays = useOverlays();

  /*
   * The one global listener in the application.
   *
   * On `document` in the capture phase, so it beats a page's own key handling — the reconcile
   * queue and the arrears register both bind arrow keys and Enter on a focused list, and Ctrl+K
   * has to work while somebody is standing in one of them. `preventDefault` is not optional: Cmd+K
   * focuses the address bar in some browsers, and Ctrl+K is "delete to end of line" inside a field
   * on macOS.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (
        !opensSearch({
          key: event.key,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          altKey: event.altKey,
          isEditable: isEditableTarget(event.target),
        })
      ) {
        return;
      }
      event.preventDefault();
      setSearchOpen(true);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  /*
   * Warm the panel's chunk once the browser has nothing better to do.
   *
   * This is what makes the `lazy()` in `SearchDialog` free rather than a trade. A palette that showed an empty
   * frame while it fetched itself would be a worse control than one that cost 2.6 KB on first
   * paint — the whole promise of Ctrl+K is that it is instant. `requestIdleCallback` runs it after
   * the page is interactive, so it is off the critical path and in memory long before anybody
   * reaches for it.
   *
   * `setTimeout` fallback for Safari, which still does not ship `requestIdleCallback`. Two seconds
   * rather than zero, so it queues behind the first screen's own data.
   */
  useEffect(() => {
    const warm = () => {
      void import('./SearchPanel');
    };

    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(warm);
      return () => {
        cancelIdleCallback(handle);
      };
    }

    const timer = setTimeout(warm, 2_000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  /*
   * The dialog is in the shell's overlays chunk, not here — see `DeferredOverlay`. A Ctrl+K pressed
   * before that chunk has landed asks for it at once, and the palette opens when it arrives; if it
   * cannot arrive, the palette closes again with a notice rather than staying "open" with nothing
   * on screen.
   */
  useEffect(() => {
    if (!open || overlays) return;
    loadOverlays().catch(() => {
      setSearchOpen(false);
      reportOverlayFailure('Could not open search');
    });
  }, [open, overlays]);

  return overlays ? <overlays.SearchDialog open={open} /> : null;
}
